"use client"

import { useState } from "react"
import {
  ChevronDown,
  ChevronRight,
  Lightbulb,
  Brain,
  AlertTriangle,
  Scale,
  Clock,
  Target,
  Database,
  MessageSquare,
  Plus,
} from "lucide-react"
import { cn } from "@/lib/utils"
import type { DecisionSnapshot, InformationalTransparency, TransparencyData } from "@/lib/types/decision-snapshot"
import type { MessageType } from "@/lib/types/reasoning"
import { logReasoningPanelOpened } from "@/lib/analytics/reasoning"

interface TransparencyPanelProps {
  transparency: TransparencyData
  messageId?: string
  messageType?: MessageType
}

/**
 * New Transparency Panel - Advisor-grade decision reasoning
 * Implements tier-one transparency aligned with Four Pillar Framework
 */
export function TransparencyPanel({ transparency, messageId, messageType = "general" }: TransparencyPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  if (!transparency) return null

  const handleExpand = () => {
    const newExpanded = !isExpanded
    setIsExpanded(newExpanded)

    if (newExpanded && messageId) {
      logReasoningPanelOpened(messageId, messageType, transparency)
    }
  }

  // Informational responses get lightweight display
  if (transparency.responseClass === "informational") {
    return (
      <InformationalTransparencyPanel transparency={transparency} isExpanded={isExpanded} onToggle={handleExpand} />
    )
  }

  // Advisory responses get full decision snapshot
  return <AdvisoryTransparencyPanel snapshot={transparency} isExpanded={isExpanded} onToggle={handleExpand} />
}

/**
 * Lightweight transparency for informational responses
 */
function InformationalTransparencyPanel({
  transparency,
  isExpanded,
  onToggle,
}: {
  transparency: InformationalTransparency
  isExpanded: boolean
  onToggle: () => void
}) {
  return (
    <div className="mt-3 pt-3 border-t border-border/30">
      <button
        onClick={onToggle}
        className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors group"
      >
        {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        <Lightbulb className="h-3.5 w-3.5 text-amber-400/70" />
        <span className="font-medium">See how I decided this</span>
      </button>

      {isExpanded && (
        <div className="mt-3 space-y-4 pl-5">
          {/* Sources */}
          <TransparencySection
            icon={Database}
            title="What I considered"
            items={transparency.sources.map((s) => s.label)}
          />

          {/* Confidence */}
          <ConfidenceSection confidence={transparency.confidence} />

          {/* Memory References */}
          {transparency.memoryRefs.length > 0 && <MemoryReferencesSection memoryRefs={transparency.memoryRefs} />}
        </div>
      )}
    </div>
  )
}

/**
 * Full transparency for advisory responses
 */
function AdvisoryTransparencyPanel({
  snapshot,
  isExpanded,
  onToggle,
}: {
  snapshot: DecisionSnapshot
  isExpanded: boolean
  onToggle: () => void
}) {
  return (
    <div className="mt-3 pt-3 border-t border-border/30">
      <button
        onClick={onToggle}
        className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors group"
      >
        {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        <Lightbulb className="h-3.5 w-3.5 text-amber-400/70" />
        <span className="font-medium">See how I decided this</span>
      </button>

      {isExpanded && (
        <div className="mt-3 space-y-5 pl-5">
          {/* A. What I considered (Logos) */}
          <TransparencySection
            icon={Brain}
            title="What I considered"
            items={snapshot.considered.factors}
            sources={snapshot.considered.sources}
          />

          {/* B. Assumptions and gaps (Logos) */}
          {(snapshot.assumptionsAndGaps.assumptions.length > 0 || snapshot.assumptionsAndGaps.gaps.length > 0) && (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <AlertTriangle className="h-3 w-3" />
                Assumptions and gaps
              </h4>
              {snapshot.assumptionsAndGaps.assumptions.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs text-muted-foreground/80">Assumptions I made:</p>
                  <ul className="space-y-1.5">
                    {snapshot.assumptionsAndGaps.assumptions.slice(0, 3).map((assumption, i) => (
                      <li key={i} className="text-xs text-foreground/80 flex items-start gap-2">
                        <span className="text-amber-400/70 mt-0.5">?</span>
                        <span>{assumption.assumption}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {snapshot.assumptionsAndGaps.gaps.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-muted-foreground/80 mb-1.5">What I'd need to confirm:</p>
                  <ul className="space-y-1.5">
                    {snapshot.assumptionsAndGaps.gaps.slice(0, 3).map((gap, i) => (
                      <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                        <span className="text-amber-400/70 mt-0.5">•</span>
                        <span>{gap}</span>
                      </li>
                    ))}
                  </ul>
                  {snapshot.userControls.canAddMissingInfo && (
                    <button className="mt-2 text-xs text-[var(--brain)] hover:underline flex items-center gap-1">
                      <Plus className="h-3 w-3" />
                      Add missing info
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* C. Trade-offs and options (Logos) */}
          {snapshot.tradeoffs.options.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <Scale className="h-3 w-3" />
                Trade-offs and options
              </h4>
              <div className="space-y-2">
                {snapshot.tradeoffs.options.map((option, i) => (
                  <div
                    key={i}
                    className={cn(
                      "text-xs p-2 rounded-md border",
                      option.isRecommended
                        ? "border-[var(--brain)]/30 bg-[var(--brain-subtle)]"
                        : "border-border/50 bg-[var(--surface-elevated)]",
                    )}
                  >
                    <div className="flex items-start gap-2">
                      {option.isRecommended && <Target className="h-3 w-3 text-[var(--brain)] mt-0.5 shrink-0" />}
                      <div className="flex-1">
                        <p className="font-medium text-foreground">{option.option}</p>
                        <p className="text-muted-foreground mt-0.5">Trade-off: {option.tradeoff}</p>
                        {option.isRecommended && option.recommendationReason && (
                          <p className="text-[var(--brain)] mt-1">Why: {option.recommendationReason}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* D. Values and constraints (Axiology) */}
          {snapshot.values.valuesConsidered.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <Scale className="h-3 w-3" />
                Values and constraints
              </h4>
              <div className="space-y-2">
                {snapshot.values.valuesConsidered.map((value, i) => (
                  <div key={i} className="text-xs">
                    <p className="font-medium text-foreground">{value.value}</p>
                    <p className="text-muted-foreground mt-0.5">{value.influence}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* E. Timing and sequencing (Kairos) */}
          <div className="space-y-2">
            <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
              <Clock className="h-3 w-3" />
              Timing and sequencing
            </h4>
            <div className="space-y-2">
              <div className="text-xs">
                <p className="font-medium text-foreground">Now</p>
                <p className="text-muted-foreground">{snapshot.timing.now}</p>
              </div>
              <div className="text-xs">
                <p className="font-medium text-foreground">Next</p>
                <p className="text-muted-foreground">{snapshot.timing.next}</p>
              </div>
              <div className="text-xs">
                <p className="font-medium text-foreground">Later</p>
                <p className="text-muted-foreground">{snapshot.timing.later}</p>
              </div>
              <div className="text-xs mt-2 p-2 rounded-md bg-[var(--surface-elevated)] border border-border/50">
                <p className="text-foreground">
                  <span className="font-medium">Why now:</span> {snapshot.timing.whyNow}
                </p>
              </div>
            </div>
          </div>

          {/* F. Confidence (Logos) */}
          <ConfidenceSection confidence={snapshot.confidence} />

          {/* Memory References */}
          {snapshot.memoryRefs.length > 0 && <MemoryReferencesSection memoryRefs={snapshot.memoryRefs} />}

          {/* User Controls (Mathesis) */}
          <div className="pt-3 border-t border-border/30 flex flex-wrap gap-2">
            {snapshot.userControls.canChallenge && (
              <button className="text-xs px-3 py-1.5 rounded-md bg-[var(--surface-elevated)] hover:bg-[var(--surface-elevated)]/80 border border-border/50 text-foreground transition-colors">
                <MessageSquare className="h-3 w-3 inline mr-1" />
                Challenge an assumption
              </button>
            )}
            {snapshot.userControls.canCorrect && (
              <button className="text-xs px-3 py-1.5 rounded-md bg-[var(--surface-elevated)] hover:bg-[var(--surface-elevated)]/80 border border-border/50 text-foreground transition-colors">
                Correct something
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Reusable section component
 */
function TransparencySection({
  icon: Icon,
  title,
  items,
  sources,
}: {
  icon: any
  title: string
  items: string[]
  sources?: any[]
}) {
  return (
    <div className="space-y-2">
      <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
        <Icon className="h-3 w-3" />
        {title}
      </h4>
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li key={i} className="text-xs text-foreground/80 flex items-start gap-2">
            <span className="text-[var(--brain)] mt-0.5">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
      {sources && sources.length > 0 && (
        <div className="mt-2 text-xs text-muted-foreground">
          <p className="mb-1">Sources:</p>
          <div className="flex flex-wrap gap-1.5">
            {sources.map((source, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[var(--surface-elevated)] text-foreground/70"
              >
                {source.label}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Confidence display component
 */
function ConfidenceSection({ confidence }: { confidence: any }) {
  const confidenceColors = {
    high: "text-emerald-400",
    medium: "text-amber-400",
    low: "text-orange-400",
  }

  return (
    <div className="space-y-2">
      <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
        <Target className="h-3 w-3" />
        Confidence
      </h4>
      <div className="text-xs">
        <p className={cn("font-medium capitalize", confidenceColors[confidence.level])}>
          {confidence.level} confidence
        </p>
        <ul className="mt-1.5 space-y-1">
          {confidence.reasons.map((reason: string, i: number) => (
            <li key={i} className="text-muted-foreground flex items-start gap-2">
              <span className="text-[var(--brain)] mt-0.5">•</span>
              <span>{reason}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

/**
 * Memory references display
 */
function MemoryReferencesSection({ memoryRefs }: { memoryRefs: any[] }) {
  return (
    <div className="space-y-2">
      <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
        <Database className="h-3 w-3" />
        Business Brain items used
      </h4>
      <div className="space-y-1.5">
        {memoryRefs.map((ref, i) => (
          <button
            key={i}
            className="w-full text-left text-xs p-2 rounded-md bg-[var(--surface-elevated)] hover:bg-[var(--surface-elevated)]/80 border border-border/50 transition-colors"
          >
            <p className="font-medium text-foreground">{ref.label}</p>
            <p className="text-muted-foreground mt-0.5">{ref.valuePreview}</p>
          </button>
        ))}
      </div>
    </div>
  )
}
